import * as marietta from '../../..';
import { auxiliaryConstructions, auxiliaryMiddleCategory } from '../../manager/MasterManager';

const MasterType = {
  Company: 1,
  Default: 0,
};

const ACCENT_COLOR = '#f8a60f';

/**
 * 付帯工事選択ダイアログ（見積画面用）
 * 付帯工事テーブルの付帯工事名セルをクリックした際に表示する
 */
class QuotationAuxiliarySelectDialog {
  private guid: string;

  private onSelect: (construction: auxiliaryConstructions, middleCategory: string, subCategory: string, masterType:number ) => void;

  private masterType: number = MasterType.Company;

  private selectedMiddle: string = '';

  private selectedSub: string = '';

  private selectedConstruction: string = '';

  private searchWord: string = '';

  private readonly DIALOG_ID = 'quotationAuxiliarySelectDialog';

  private readonly OVERLAY_ID = 'quotationAuxiliarySelectOverlay';

  private onOutsideClick = (e: MouseEvent) => {
    const dialog = document.getElementById(this.DIALOG_ID);
    if (dialog && !dialog.contains(e.target as Node)) {
      this.close();
    }
  };

  open(
    guid: string,
    anchorEl: HTMLElement,
    onSelect: (construction: auxiliaryConstructions, middleCategory: string, subCategory: string, masterType: number) => void,
  ) {
    this.guid = guid;
    this.onSelect = onSelect;
    this.masterType = MasterType.Company;
    this.searchWord = '';
    this.selectedMiddle = '';
    this.selectedSub = '';
    this.selectedConstruction = '';

    this.close();
    this.render(anchorEl);
    this.setEvents();
  }

  close() {
    const dialog = document.getElementById(this.DIALOG_ID);
    if (dialog) dialog.remove();
    document.removeEventListener('mousedown', this.onOutsideClick);
  }

  private getMasterList(): auxiliaryMiddleCategory[] {
    const master = marietta.webcad.WebCADGlobal.AUXILIARY_MASTER;
    if (!master) return [];
    return this.masterType === MasterType.Company
      ? (master.company || [])
      : (master.default || []);
  }

  private render(anchorEl: HTMLElement) {
    const rect = anchorEl.getBoundingClientRect();

    // いったん画面外に配置してサイズを取得してから位置を確定する
    const dialog = document.createElement('div');
    dialog.id = this.DIALOG_ID;
    dialog.style.cssText = `
      position:fixed;top:-9999px;left:-9999px;
      z-index:9999;background:#fff;box-shadow:0 0 10px #aaa;
      font-size:13px;padding:8px;
    `;
    dialog.innerHTML = this.buildStaticHtml();
    document.body.appendChild(dialog);

    // 実際のサイズを取得して位置を補正
    const dlgRect = dialog.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const margin = 8;

    // 下方向：アンカーの下に収まるか、収まらなければアンカーの上に表示
    let top: number;
    const topBelow = rect.bottom + 5;
    const topAbove = rect.top - dlgRect.height - 5;
    if (topBelow + dlgRect.height + margin <= vh) {
      top = topBelow;
    } else if (topAbove >= margin) {
      top = topAbove;
    } else {
      // どちらにも収まらない場合は画面内に収める
      top = Math.max(margin, vh - dlgRect.height - margin);
    }

    // 右方向：はみ出す場合は左にずらす
    let { left } = rect;
    if (left + dlgRect.width + margin > vw) {
      left = Math.max(margin, vw - dlgRect.width - margin);
    }

    dialog.style.top = `${top}px`;
    dialog.style.left = `${left}px`;

    // ダイアログ外クリックで閉じる（次のイベントループで登録してself-closeを防ぐ）
    setTimeout(() => {
      document.addEventListener('mousedown', this.onOutsideClick);
    }, 0);

    this.updateListArea();
  }

  /** タブ・検索ボックス・検索ボタンなど、再描画しない固定部分 */
  private buildStaticHtml(): string {
    const inactiveTabBg = '#DDD';
    return `
      <!-- マスタ切り替えタブ -->
      <div id="auxDlgTabWrap" style="display:flex;margin-bottom:6px;">
        <div id="auxDlgTabCompany" data-tab="1" style="
          flex:1;height:28px;line-height:28px;text-align:center;cursor:pointer;
          background:${this.masterType === MasterType.Company ? ACCENT_COLOR : inactiveTabBg};
          color:#fff;border:1px solid #bfbfbf;
        ">契約企業専用</div>
        <div id="auxDlgTabDefault" data-tab="0" style="
          flex:1;height:28px;line-height:28px;text-align:center;cursor:pointer;
          background:${this.masterType === MasterType.Default ? ACCENT_COLOR : inactiveTabBg};
          color:#fff;border:1px solid #bfbfbf;border-left:none;
        ">デフォルト</div>
      </div>
      <!-- 検索 -->
      <div style="display:flex;margin-bottom:6px;gap:4px;">
        <input id="auxDlgSearch" type="text" placeholder="付帯工事名で検索" autocomplete="off"
          style="flex:1;box-sizing:border-box;padding:4px 6px;border:1px solid #ccc;height:28px;" />
        <button id="auxDlgSearchBtn" style="
          height:28px;padding:0 10px;cursor:pointer;
          background:${ACCENT_COLOR};color:#fff;border:none;white-space:nowrap;
        ">検索</button>
      </div>
      <!-- カテゴリリスト（差し替え対象） -->
      <div id="auxDlgListArea" style="display:flex;height:300px;width:600px;"></div>
    `;
  }

  /** カテゴリリスト部分のみを差し替える */
  private updateListArea() {
    const area = document.getElementById('auxDlgListArea');
    if (!area) return;
    area.innerHTML = this.buildListAreaHtml();
    this.setListEvents();
  }

  private buildListAreaHtml(): string {
    const masterList = this.getMasterList();
    const subList = masterList.find((m) => m.middle_category_name === this.selectedMiddle)?.sub_categories || [];
    const constructionList = subList.find((s) => s.sub_category_name === this.selectedSub)?.auxiliary_constructions || [];
    const searchResults = this.searchWord ? this.getSearchResults() : [];

    if (this.searchWord !== '') {
      return `<div id="auxDlgSearchList" style="${QuotationAuxiliarySelectDialog.listStyle()}">${QuotationAuxiliarySelectDialog.buildSearchListHtml(searchResults)}</div>`;
    }

    return `
      <!-- 中分類 -->
      <div id="auxDlgMiddleList" style="${QuotationAuxiliarySelectDialog.listStyle()}">
        ${masterList.map((m) => `
          <div data-middle="${QuotationAuxiliarySelectDialog.escapeHtml(m.middle_category_name)}" class="auxDlgItem" style="
            padding:6px 8px;cursor:pointer;
            ${this.selectedMiddle === m.middle_category_name ? `background:${ACCENT_COLOR};color:#fff;` : ''}
          ">${QuotationAuxiliarySelectDialog.escapeHtml(m.middle_category_name)}</div>
        `)
    .join('')}
      </div>
      <!-- 細分類 -->
      <div id="auxDlgSubList" style="${QuotationAuxiliarySelectDialog.listStyle(true)}">
        ${subList.map((s) => `
          <div data-sub="${QuotationAuxiliarySelectDialog.escapeHtml(s.sub_category_name)}" class="auxDlgItem" style="
            padding:6px 8px;cursor:pointer;
            ${this.selectedSub === s.sub_category_name ? `background:${ACCENT_COLOR};color:#fff;` : ''}
          ">${QuotationAuxiliarySelectDialog.escapeHtml(s.sub_category_name)}</div>
        `)
    .join('')}
      </div>
      <!-- 付帯工事 -->
      <div id="auxDlgConstructionList" style="${QuotationAuxiliarySelectDialog.listStyle(true)}">
        ${constructionList.map((c) => `
          <div data-construction-id="${QuotationAuxiliarySelectDialog.escapeHtml(c.id)}" class="auxDlgItem" style="
            padding:6px 8px;cursor:pointer;
            ${this.selectedConstruction === c.id ? `background:${ACCENT_COLOR};color:#fff;` : ''}
          ">${QuotationAuxiliarySelectDialog.escapeHtml(c.name)}</div>
        `)
    .join('')}
      </div>
    `;
  }

  private static listStyle(noBorderLeft = false): string {
    return `
      width:200px;min-width:200px;overflow-y:auto;
      background:#fff;border:2px solid #bfbfbf;
      box-sizing:border-box;height:100%;
      ${noBorderLeft ? 'border-left:none;' : ''}
    `;
  }

  private getSearchResults(): Array<{ construction: auxiliaryConstructions; middle: string; sub: string }> {
    const word = this.searchWord.toLowerCase();
    return this.getMasterList()
      .flatMap((middle) => middle.sub_categories.flatMap((sub) => sub.auxiliary_constructions
        .filter((construction) => construction.name.toLowerCase()
          .includes(word))
        .map((construction) => ({
          construction,
          middle: middle.middle_category_name,
          sub: sub.sub_category_name,
        }))));
  }

  private static buildSearchListHtml(results: Array<{
    construction: auxiliaryConstructions;
    middle: string;
    sub: string
  }>): string {
    if (results.length === 0) {
      return '<div style="padding:8px;color:#999;">検索結果がありません。</div>';
    }
    return results.map((r) => `
      <div data-construction-id="${QuotationAuxiliarySelectDialog.escapeHtml(r.construction.id)}"
           data-middle="${QuotationAuxiliarySelectDialog.escapeHtml(r.middle)}"
           data-sub="${QuotationAuxiliarySelectDialog.escapeHtml(r.sub)}"
           class="auxDlgItem" style="padding:6px 8px;cursor:pointer;">
        ${QuotationAuxiliarySelectDialog.escapeHtml(r.construction.name)}
      </div>
    `)
      .join('');
  }

  /** ダイアログ全体の固定部分のイベント（初回のみ設定） */
  private setEvents() {
    const dialog = document.getElementById(this.DIALOG_ID);
    if (!dialog) return;

    // タブ切り替え
    dialog.querySelectorAll<HTMLElement>('#auxDlgTabCompany, #auxDlgTabDefault')
      .forEach((el) => {
        el.addEventListener('click', () => {
          this.masterType = Number(el.dataset.tab);
          this.selectedMiddle = '';
          this.selectedSub = '';
          this.selectedConstruction = '';
          this.searchWord = '';
          // タブのアクティブ色を切り替え
          dialog.querySelector<HTMLElement>('#auxDlgTabCompany').style.background = this.masterType === MasterType.Company ? ACCENT_COLOR : '#DDD';
          dialog.querySelector<HTMLElement>('#auxDlgTabDefault').style.background = this.masterType === MasterType.Default ? ACCENT_COLOR : '#DDD';
          const searchInput = dialog.querySelector<HTMLInputElement>('#auxDlgSearch');
          if (searchInput) searchInput.value = '';
          this.updateListArea();
        });
      });

    // 検索ボタン
    const searchBtn = dialog.querySelector<HTMLButtonElement>('#auxDlgSearchBtn');
    if (searchBtn) {
      searchBtn.addEventListener('click', () => {
        const searchInput = dialog.querySelector<HTMLInputElement>('#auxDlgSearch');
        this.searchWord = searchInput?.value || '';
        this.updateListArea();
      });
    }
  }

  /** リスト部分のイベント（updateListArea後に再設定） */
  private setListEvents() {
    // 中分類クリック
    document.querySelectorAll<HTMLElement>('#auxDlgMiddleList .auxDlgItem')
      .forEach((el) => {
        el.addEventListener('click', () => {
          this.selectedMiddle = el.dataset.middle || '';
          this.selectedSub = '';
          this.selectedConstruction = '';
          this.updateListArea();
        });
      });

    // 細分類クリック
    document.querySelectorAll<HTMLElement>('#auxDlgSubList .auxDlgItem')
      .forEach((el) => {
        el.addEventListener('click', () => {
          this.selectedSub = el.dataset.sub || '';
          this.selectedConstruction = '';
          this.updateListArea();
        });
      });

    // 付帯工事選択
    document.querySelectorAll<HTMLElement>('#auxDlgConstructionList .auxDlgItem')
      .forEach((el) => {
        el.addEventListener('click', () => {
          this.handleSelectConstruction(el.dataset.constructionId, this.selectedMiddle, this.selectedSub);
        });
      });

    // 検索結果から選択
    document.querySelectorAll<HTMLElement>('#auxDlgSearchList .auxDlgItem')
      .forEach((el) => {
        el.addEventListener('click', () => {
          this.handleSelectConstruction(el.dataset.constructionId, el.dataset.middle, el.dataset.sub);
        });
      });
  }

  private handleSelectConstruction(constructionId: string, middleName: string, subName: string) {
    const found = this.getMasterList()
      .flatMap((middle) => middle.sub_categories.flatMap((sub) => sub.auxiliary_constructions))
      .find((construction) => construction.id === constructionId);
    if (!found) return;

    this.selectedConstruction = found.id;
    this.onSelect(found, middleName, subName, this.masterType );
    this.close();
  }

  private static escapeHtml(str: string): string {
    if (!str) return '';
    return str.replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

export default QuotationAuxiliarySelectDialog;
